import type { GetOrderResponse, UbaPlaceOrder } from './types'

import { useMutation, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import http from '../../lib/http'

export let formatterPrice = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

export enum Gender {
  Male = 'Male',
  Female = 'Female',
  Other = 'Other',
}

export const patientSchema = z.object({
  patientId: z.string({ message: 'Patient ID is required and cannot be empty.' }),

  patientGroup: z.string({ message: 'Patient Group is required and cannot be empty.' })
    .optional(),

  firstName:
      z.string({ message: 'First Name is required and cannot be empty.' })
        .min(1, 'First Name is required and cannot be empty.'),

  lastName: z.string({ message: 'Last Name is required and cannot be empty.' })
    .min(1, 'Last Name is required and cannot be empty.'),

  addressLine1: z.string({ message: 'Address Line 1 is required and cannot be empty.' })
    .min(1, 'Address Line 1 is required and cannot be empty.'),

  addressLine2: z.string({ message: 'Address Line 2 is required.' })
    .nullish(),

  cityName: z.string({ message: 'City Name is required and cannot be empty.' })
    .min(1, 'City Name is required and cannot be empty.'),

  dob: z.string({ message: 'Date of Birth is required and must be a valid date.' })
    .min(1, 'Date of Birth is required and must be a valid date.'),

  countryCode: z.string({ message: 'Country Code is required and cannot be empty.' })
    .min(1, 'Country Code is required and cannot be empty.'),

  currentCondition: z.string({ message: 'Current Condition is required and cannot be empty.' })
    .min(1, 'Current Condition is required and cannot be empty.'),

  allergies: z.string({ message: 'Allergies field is required. If none, specify "None."' })
    .min(1, 'Allergies field is required and cannot be empty.'),

  isBillingAddress: z.boolean({ message: 'Billing Address information is required.' })
    .nullish(),

  medicalCondition: z.string({ message: 'Medical Condition is required. If none, specify "None."' })
    .min(1, 'Medical Condition is required and cannot be empty.'),

  patientNotes: z.string({ message: 'Patient Notes are required. If none, leave empty.' }).optional(),

  phone: z.string({ message: 'Phone number is required and must be valid.' })
    .min(1, 'Phone number is required and cannot be empty.'),

  stateCode: z.string({ message: 'State Code is required and cannot be empty.' })
    .min(1, 'State Code is required and cannot be empty.'),

  email: z.string({ message: 'Email is required and must be a valid email address.' })
    .min(1, 'Email is required and cannot be empty.'),

  zipCode: z.string({ message: 'Zip Code is required and must be a valid postal code.' })
    .min(1, 'Zip Code is required and cannot be empty.'),

  ubaPatientId: z.string({ message: 'UBA Patient ID is required and cannot be empty.' }).optional(),

  gender: z.enum(Object.values(Gender) as [string, string], { message: 'Gender is required and cannot be empty.' })
})

export const newPatientSchema = patientSchema.omit({ ubaPatientId: true })
export const updatePatientSchema = patientSchema

export const ubaPatientSchema = z.object({
  addressLine1: z.string(),
  addressLine2: z.string(),
  allergies: z.string(),
  cityName: z.string(),
  clientName: z.string(),
  countryName: z.string(),
  createdBy: z.string(),
  createdDate: z.string(),
  currentCondition: z.string(),
  customerId: z.string(),
  dob: z.string(),
  email: z.string(),
  gender: z.string(),
  isActive: z.boolean(),
  isBilling: z.boolean(),
  medicalCondition: z.string(),
  modifiedBy: z.string(),
  modifiedDate: z.string(),
  patientGroup: z.string(),
  patientId: z.string(),
  patientName: z.string(),
  patientNotes: z.string(),
  patientProofPath: z.string(),
  patientUserId: z.string(),
  phone: z.string(),
  proofName: z.string(),
  proofOriginalName: z.string().nullable(),
  stateName: z.string(),
  stateCode: z.string().optional(),
  zipCode: z.string(),
  patientGUID: z.string()
})

export type Patient = z.infer<typeof patientSchema>
export type UbaPatient = z.infer<typeof ubaPatientSchema>


export function useUbaCreateMutation () {
  return useMutation<unknown, unknown, { id: string }, unknown>({
    async mutationFn (variables) {
      return await http.postRequest(`/uba/${variables.id}`, {}, {
        validateStatus: (status: number) => status >= 200 && status < 300
      })
    }
  })
}


export function useUbaUpdateMutation () {
  return useMutation<unknown, unknown, { data: Patient, id: string }, unknown>({
    async mutationFn (variables) {
      return await http.putRequest(`/uba/${variables.id}`, variables.data, {
        validateStatus: (status: number) => status >= 200 && status < 300
      })
    }
  })
}


export const useUbaPlaceOrderMutation = () => {
  return useMutation<unknown, unknown, UbaPlaceOrder, unknown>({
    async mutationFn ({ patientId, ...variables }) {
      const uploads = await Promise.all(variables.files.map(async (file) => {
        const formData = new FormData()

        formData.append('file0', file, file.name)

        return await http.postRequest<{
          files: {
            file0: {
              id: string
              mime_type: string
              filename: string
            }
          }
        }>('/documents', formData)
      }))

      const files = uploads.map((u) => ({
        id: u.files.file0.id,
        mimeType: u.files.file0.mime_type,
        fileName: u.files.file0.filename
      }))

      return await http.postRequest(`/uba/${patientId}/placeorder`, { ...variables, files }, {
        validateStatus: (status: number) => status >= 200 && status < 300
      })
    }
  })
}


export const useUbaOrders = (patientId: string) => {
  return useQuery({
    queryKey: [ 'ubaOrders', patientId ],
    queryFn: async (): Promise<GetOrderResponse[]> => {
      return await http.getRequest<GetOrderResponse[]>(`/uba/${patientId}/orders`, {}, {
        validateStatus: (status: number) => status >= 200 && status < 300
      })
    }
  })
}
